import classNames from "classnames";
import { useCallback, useState } from "react";

import { LISCase, LISCaseList } from "pwapi";

import { useRequestAfterEverySiteLoad } from "shared/hooks/requestStatusHooks";
import { selectFeatureFlag } from "shared/reducers/sysReducer";

import { scanBlock } from "app/actions/blockActions";
import { listCases } from "app/actions/caseActions";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { workWithTaskIn } from "app/selectors/workSelectors";
import useBarcodeScanner from "hooks/useBarcodeScanner";
import {
  useSelectedLab,
  useSelectedLabStation,
} from "hooks/useSelectedLabStation";
import AppSpinner from "ui/components/AppSpinner";
import CaseListItem from "ui/lists/CaseListItem";
import CaseReadyItem from "ui/lists/CaseReadyItem";
import { labelForBlock } from "util/blockUtils";
import getIncompleteTask from "util/getIncompleteTask";
import { blockSort, specimenSort } from "util/sortFunctions";

import ScannedBlock from "./ScannedBlock";
import TaskCount from "../ui/TaskCount";

const Block = ({
  block,
  pathCase,
  onScanBlock,
  workId,
}: {
  block: LISCaseList["blocks"][0];
  pathCase: LISCaseList;
  onScanBlock: (id: number, workId?: number) => void;
  workId?: number;
}) => {
  const lisAltClickScanning = useAppSelector((state) =>
    selectFeatureFlag(state, "lisAltClickScanning")
  );

  return (
    <h2
      className={classNames(
        "EmbeddingBlock",
        `EmbeddingBlock--${block.lis_state}`,
        "Scannable"
      )}
      onClick={(e) => {
        if (lisAltClickScanning && e.altKey && block.id) {
          onScanBlock(block.id, workId);
        }
      }}
    >
      <code>{labelForBlock(block, pathCase)}</code>
    </h2>
  );
};

const Case = ({
  pathCase,
  block_state,
  onScanBlock,
}: {
  pathCase: LISCaseList;
  block_state: string;
  onScanBlock: (id: number, workId?: number) => void;
}) => {
  const task = getIncompleteTask(pathCase, "embedding");
  const block_states =
    block_state === "processed" ? [block_state, "racked"] : [block_state]; // old LIS workflow left blocks in 'racked' state after grossing

  if (!pathCase.id) return null;

  const specimens = [...pathCase.specimens].sort(specimenSort);

  const blocks = pathCase.blocks.filter((b) =>
    block_states.includes(b.lis_state || "")
  );

  let label = `${blocks.length} Block${blocks.length === 1 ? "" : "s"}`;
  const difSpecimenCount = specimens.filter((s) =>
    // @ts-ignore
    s.protocols?.includes("dif")
  ).length;
  if (difSpecimenCount)
    label += ` & ${difSpecimenCount} DIF Specimen${
      difSpecimenCount === 1 ? "" : "s"
    }`;

  return (
    <CaseListItem id={pathCase.id} label={label}>
      {specimens.map((s) =>
        blocks
          .filter((b) => b.specimen_id === s.id)
          .sort(blockSort)
          .map((b) => (
            <Block
              block={b}
              key={b.id}
              onScanBlock={onScanBlock}
              workId={task?.work_id}
              pathCase={pathCase}
            />
          ))
      )}
    </CaseListItem>
  );
};

const EmbeddingStationList = () => {
  const dispatch = useAppDispatch();
  const lab = useSelectedLab();
  const cases = useAppSelector(workWithTaskIn(["processing", "embedding"]));
  const outCases = useAppSelector(workWithTaskIn("cutting"));
  const blocks = cases.flatMap((c) => c.blocks);
  const selectedLabStation = useSelectedLabStation();

  const [scanned, setScanned] = useState<{
    block: LISCase["blocks"][0];
    specimen: LISCase["specimens"][0];
    case: LISCase;
  }>();

  const allCooling = blocks.every((b) => b.lis_state === "cooling");

  const embeddingWorks = cases.filter(
    (w) =>
      w.blocks.some((b) => ["processed", "racked"].includes(b.lis_state || "")) // old LIS workflow left blocks in 'racked' state after grossing
  );

  const coolingWorks = cases.filter((w) =>
    w.blocks.some((b) => b.lis_state === "cooling")
  );

  const onScanBlock = useCallback(
    (id: number) => {
      dispatch(
        scanBlock({
          id,
          parentLookupLabStationId: selectedLabStation.id,
        })
      ).then((response: LISCase) => {
        const block = response.blocks.find((b) => b.id === id);
        const specimen = response.specimens.find(
          (s) => s.id === block?.specimen_id
        );
        // Block will have lis_state === "cooling" after it is scanned for the first time at this station
        // If we are "single scan embedding", the state coming back will be "embedded", so we want to
        // ... still display the scanned block information on the screen, even though the work is finished
        if (
          (!!block && !!specimen && block.lis_state === "cooling") ||
          (lab?.settings?.single_scan_embedding?.active &&
            !!block &&
            !!specimen &&
            block?.lis_state === "embedded")
        ) {
          setScanned({ block, specimen, case: response });
        } else if (!!block && !!scanned && block.id !== scanned.block.id) {
          setScanned(undefined);
        }
        return response;
      });
    },
    [blocks, selectedLabStation.id]
  );

  useBarcodeScanner(({ object_type, object_id }) => {
    if (object_type === "block") {
      onScanBlock(object_id);
    } else {
      return false;
    }
  });

  const casesReady = useRequestAfterEverySiteLoad(listCases, {
    parentLookupLabStationId: selectedLabStation.id,
  });

  if (!casesReady) return <AppSpinner>Loading Cases...</AppSpinner>;

  return (
    <div className="Station Split">
      <main>
        <header className="Header">
          <figure className="Logo">
            <img src="/logo.svg" alt="" />
          </figure>
          <p className="Label">{selectedLabStation.name}</p>

          <TaskCount cases={cases} stationType={selectedLabStation.type} />
        </header>

        {!!embeddingWorks?.length && (
          <div className="WorkList WorkList--embedding">
            <h2>Ready for Embedding</h2>
            {embeddingWorks.map((c) => (
              <Case
                key={c.id}
                pathCase={c}
                block_state="processed"
                onScanBlock={onScanBlock}
              />
            ))}
          </div>
        )}

        {!!coolingWorks?.length && (
          <div className="WorkList WorkList--cooling">
            <h2>Cooling</h2>
            {coolingWorks.map((c) => (
              <Case
                key={c.id}
                pathCase={c}
                block_state="cooling"
                onScanBlock={onScanBlock}
              />
            ))}
          </div>
        )}
      </main>
      <aside>
        {scanned && <ScannedBlock scanned={scanned} />}

        <h4>Ready for Cutting</h4>
        {outCases.map((c) => (
          <CaseReadyItem key={c.id} readyCase={c} />
        ))}
      </aside>
    </div>
  );
};

export default EmbeddingStationList;
